import { create, type StoreApi, type UseBoundStore } from 'zustand';
import {
  applyEvent,
  emptyCollection,
  type Collection,
  type Comparator,
  type Unsubscribe,
  type WatchEvent,
} from './collection';
import { getSources } from './sources';
import type { LiveSource } from './sources/types';

/* The lifecycle every watched collection shares.
 *
 * `open(query)` is the whole contract: it stops whatever was running, opens a
 * new generation, and starts a watch. Events from any earlier watch are dropped,
 * which is what stops a slow answer to an abandoned filter from overwriting the
 * filter the operator is now looking at.
 *
 * Rows stay on screen throughout. `open` moves the phase to `loading` and
 * touches nothing else; the visible ids are replaced only when the new query's
 * initial result actually lands.
 *
 * Three lifecycle hazards are handled here rather than hoped away:
 *
 *   The generation counter lives in this closure and only ever increases. Taking
 *   it from `data.gen` looked equivalent and was not: `reset()` puts back an
 *   empty collection at generation 0, so the next `open()` would reissue a
 *   generation an in-flight callback from before the reset still carries, and
 *   that callback would be accepted as current.
 *
 *   A callback is checked against the generation that is CURRENTLY live, not
 *   just against the collection's. Unsubscribing does not retract work a source
 *   has already queued — the fixture source resolves through a microtask, and
 *   Firestore's own listener can deliver one more snapshot — so `close()` has to
 *   invalidate as well as unsubscribe.
 *
 *   The unsubscribe handle is only stored if it still belongs to the live
 *   generation. A source that calls back synchronously can re-enter `open()`
 *   before `watch()` returns, and assigning its handle afterwards would stomp
 *   the newer subscription and leak the older one. */

export interface LiveStore<T, Q> {
  data: Collection<T>;
  /** The query the current generation was opened with, for re-opens. */
  query: Q | undefined;
  open(query: Q): void;
  /** Stop watching but keep the rows — used when a screen unmounts. */
  close(): void;
  /** Drop everything. Used on sign-out and by the preview harness on unmount. */
  reset(): void;
  /** Fixture seeding. A live watch never calls this. */
  hydrate(c: Collection<T>): void;
}

type Watcher<T, Q> = (
  src: LiveSource,
  query: Q,
  on: (ev: WatchEvent<T>) => void,
) => Unsubscribe;

export function createLiveStore<T, Q>(
  watch: Watcher<T, Q>,
  compare?: Comparator<T>,
): UseBoundStore<StoreApi<LiveStore<T, Q>>> {
  return create<LiveStore<T, Q>>((set) => {
    // Monotonic for the life of the store. Never derived from the collection.
    let generation = 0;
    // The generation whose events are currently welcome. 0 means none.
    let live = 0;
    let unsubscribe: Unsubscribe | undefined;

    const stop = () => {
      live = 0;
      const u = unsubscribe;
      unsubscribe = undefined;
      u?.();
    };

    return {
      data: emptyCollection<T>(),
      query: undefined,

      open(query: Q) {
        stop();
        generation += 1;
        const gen = generation;
        const src = getSources().live;
        // The empty source never emits, so entering `loading` against it would
        // spin forever and read as "still fetching" — which is a different and
        // more hopeful claim than the truth, that nothing is wired.
        if (src.kind === 'empty') {
          set((s) => ({ data: { ...s.data, phase: 'unconfigured', error: undefined, gen }, query }));
          return;
        }
        live = gen;
        set((s) => ({
          data: { ...s.data, phase: 'loading', error: undefined, gen },
          query,
        }));
        const handle = watch(src, query, (ev) => {
          if (gen !== live) return;
          set((s) => ({ data: applyEvent(s.data, gen, ev, compare) }));
        });
        if (gen === live) {
          unsubscribe = handle;
        } else {
          // A synchronous callback already re-entered open(); this handle
          // belongs to a generation nobody is listening to any more.
          handle();
        }
      },

      close() {
        stop();
      },

      reset() {
        stop();
        set({ data: emptyCollection<T>(), query: undefined });
      },

      hydrate(c: Collection<T>) {
        stop();
        set({ data: c });
      },
    };
  });
}

/* The same lifecycle for a collection that arrives as a promise rather than a
 * stream — the pool and the tenant BYO keys, which cannot be watched from a
 * browser at all (spec/console.md §Two data lanes).
 *
 * It carries the same monotonic generation for the same reason: a slow list
 * answering a tenant the operator has already navigated away from must not
 * repopulate the table they are now looking at. */

export interface FetchStore<T, Q> {
  data: Collection<T>;
  load(query: Q): void;
  /** Drop one document the server has confirmed is gone.
   *
   *  A fetch-backed collection has no delta stream to tell it so, and reloading
   *  is not a substitute: if the refetch fails, `applyEvent` keeps the rows it
   *  has — correctly, because a load must never blank a table — and the deleted
   *  row stays on screen inviting a retry that can only 404. */
  drop(id: string): void;
  /** Replace ONE document, leaving every other row untouched.
   *
   *  The point is object identity, not convenience: a full `load()` builds a new
   *  object for every row, so React re-creates every row's subtree and the
   *  operator loses hover, selection and any in-place state — for a change that
   *  affected one credential. This applies a `modified` delta through the same
   *  path a watch uses, so only the changed row is new
   *  (spec/console.md §Update discipline). */
  upsert(id: string, doc: T): void;
  reset(): void;
  hydrate(c: Collection<T>): void;
}

/** A fetcher may answer a bare array or one page with the source's cursor
 *  for the next one; the cursor rides the reset event into the collection so
 *  a paged surface (memories) uses the same cursor-stack pattern the ledger
 *  does. Existing array fetchers are unchanged. */
export interface FetchPage<T> {
  items: T[];
  nextCursor?: string;
}

export function createFetchStore<T, Q>(
  fetcher: (src: LiveSource, query: Q) => Promise<T[] | FetchPage<T>>,
  idOf: (doc: T) => string,
): UseBoundStore<StoreApi<FetchStore<T, Q>>> {
  return create<FetchStore<T, Q>>((set) => {
    let generation = 0;
    let live = 0;

    return {
      data: emptyCollection<T>(),

      load(query: Q) {
        generation += 1;
        const gen = generation;
        const src = getSources().live;
        if (src.kind === 'empty') {
          set((s) => ({ data: { ...s.data, phase: 'unconfigured', error: undefined, gen } }));
          return;
        }
        live = gen;
        set((s) => ({ data: { ...s.data, phase: 'loading', error: undefined, gen } }));
        fetcher(src, query).then(
          (answer) => {
            if (gen !== live) return;
            const docs = Array.isArray(answer) ? answer : answer.items;
            const nextCursor = Array.isArray(answer) ? undefined : answer.nextCursor;
            set((s) => ({
              data: applyEvent(s.data, gen, {
                type: 'reset',
                docs: docs.map((d) => ({ id: idOf(d), doc: d })),
                nextCursor,
              }),
            }));
          },
          (err: unknown) => {
            if (gen !== live) return;
            set((s) => ({
              data: applyEvent(s.data, gen, {
                type: 'error',
                message: err instanceof Error ? err.message : String(err),
              }),
            }));
          },
        );
      },

      drop(id: string) {
        // `live` is zero until a load has been accepted, and returns to zero on
        // reset() and hydrate(). Both single-row writes check it, because they
        // are applied by a response that arrived LATER than the action that
        // asked for it: without this, a delete confirmed while the operator was
        // signing out — or while /dev fixtures were being hydrated — would edit
        // a collection that no longer belongs to the request that produced it.
        if (live === 0) return;
        // A row write makes any list request still in flight stale — it was
        // issued before this happened, so its answer would undo it. Bumping the
        // generation is how this store already retires a superseded request, so
        // the outstanding load drops its own answer on arrival.
        generation += 1;
        live = generation;
        set((s) => ({
          data: applyEvent(s.data, s.data.gen, {
            type: 'change',
            changes: [{ kind: 'removed', id }],
          }),
        }));
      },

      upsert(id: string, doc: T) {
        if (live === 0) return;
        generation += 1;
        live = generation;
        set((s) => {
          // Refresh a row that is HERE; never add one. A delayed read landing
          // after a delete would otherwise resurrect the deleted row, and a
          // resurrected credential is one an operator believes they removed.
          if (!(id in s.data.byId)) return s;
          return {
            data: applyEvent(s.data, s.data.gen, {
              type: 'change',
              changes: [{ kind: 'modified', id, doc }],
            }),
          };
        });
      },

      reset() {
        live = 0;
        set({ data: emptyCollection<T>() });
      },

      hydrate(c: Collection<T>) {
        live = 0;
        set({ data: c });
      },
    };
  });
}
